import { useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

const SESSION_CHECK_INTERVAL = 1000 * 60 * 5; // Check every 5 minutes
const SESSION_WARNING_THRESHOLD = 1000 * 60 * 10; // Warn 10 minutes before expiry

export const useSessionManager = () => {
  const { session } = useAuth();
  const warningShownRef = useRef(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const getSessionExpiryTime = useCallback(() => {
    if (!session?.expires_at) return null;
    return new Date(session.expires_at * 1000).getTime();
  }, [session]);

  const getRemainingTime = useCallback(() => {
    const expiryTime = getSessionExpiryTime();
    if (!expiryTime) return null;
    return expiryTime - Date.now();
  }, [getSessionExpiryTime]);

  const refreshSession = useCallback(async () => {
    try {
      const { data, error } = await supabase.auth.refreshSession();
      if (error) throw error;
      
      if (data.session) {
        warningShownRef.current = false;
        return true;
      }
      return false;
    } catch (error) {
      console.error('Session refresh failed:', error);
      return false;
    }
  }, []);

  const checkSession = useCallback(async () => {
    const remaining = getRemainingTime();
    
    if (!remaining) return;

    // Session expired
    if (remaining <= 0) {
      toast.error('Your session has expired. Please sign in again.');
      await supabase.auth.signOut();
      return;
    }

    // Show warning if approaching expiry
    if (remaining <= SESSION_WARNING_THRESHOLD && !warningShownRef.current) {
      warningShownRef.current = true;
      const minutes = Math.floor(remaining / 1000 / 60);
      
      toast.warning(
        `Your session will expire in ${minutes} minutes`,
        {
          duration: 10000,
          action: {
            label: 'Refresh',
            onClick: async () => {
              const success = await refreshSession();
              if (success) {
                toast.success('Session refreshed successfully');
              } else {
                toast.error('Failed to refresh session');
              }
            },
          },
        }
      );
    }

    // Auto-refresh when getting close to expiry
    if (remaining <= SESSION_WARNING_THRESHOLD / 2) {
      await refreshSession();
    }
  }, [getRemainingTime, refreshSession]);

  useEffect(() => {
    if (!session) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    // Initial check
    checkSession();

    // Set up interval
    intervalRef.current = setInterval(checkSession, SESSION_CHECK_INTERVAL);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [session, checkSession]);

  return {
    refreshSession,
    getRemainingTime,
  };
};
